import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './pages/Login';
import Layout from './components/Layout';
import AdminLayout from './components/AdminLayout';
import Dashboard from './pages/Dashboard';
import Datasets from './pages/Datasets';
import DatasetView from './pages/DatasetView';
import AllLeads from './pages/AllLeads';
import Templates from './pages/Templates';
import Reports from './pages/Reports';
import Campaigns from './pages/Campaigns';
import Settings from './pages/Settings';
import BotConfig from './pages/BotConfig';
import Inbox from './pages/Inbox';
import Channels from './pages/Channels';
import { hasFeature, firstAllowedPath } from './utils/features';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminClients from './pages/admin/AdminClients';
import AdminClientEdit from './pages/admin/AdminClientEdit';
import AdminReports from './pages/admin/AdminReports';
import AdminSettings from './pages/admin/AdminSettings';
import AgenciaPanel from './pages/agencia/Panel';
import AgenciaClientes from './pages/agencia/Clientes';
import AgenciaClienteDetalle from './pages/agencia/ClienteDetalle';
import AgenciaCobros from './pages/agencia/Cobros';
import AgenciaRenovaciones from './pages/agencia/Renovaciones';
import AgenciaEgresos from './pages/agencia/Egresos';
import AgenciaFinanzas from './pages/agencia/Finanzas';
import AgenciaAbonos from './pages/agencia/Abonos';
import AgenciaMediosPago from './pages/agencia/MediosPago';
import AgenciaPropuestas from './pages/agencia/Propuestas';
import PortalApp from './pages/portal/PortalApp';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', color:'var(--text-muted)' }}>Cargando...</div>;
  return user ? children : <Navigate to="/login" />;
}

function FeatureRoute({ feature, children }) {
  const { user, tenant } = useAuth();
  if (hasFeature(tenant, user, feature)) return children;
  const fallback = firstAllowedPath(tenant, user);
  if (fallback && fallback !== window.location.pathname) return <Navigate to={fallback} replace />;
  return <div style={{ padding: 40, color: 'var(--text-muted)' }}>No tenés funciones habilitadas. Contactá a soporte.</div>;
}

const isAdminHost = () => window.location.hostname.startsWith('admin.');
const isPortalHost = () => window.location.hostname.startsWith('clientes.');

function AppRoutes() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (isAdminHost()) {
    return (<Routes>
      <Route path="/login" element={user ? <Navigate to="/" /> : <Login />} />
      <Route path="/" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
        <Route index element={<AdminDashboard />} />
        <Route path="clients" element={<AdminClients />} />
        <Route path="clients/:id" element={<AdminClientEdit />} />
        <Route path="reports" element={<AdminReports />} />
        <Route path="agencia" element={<AgenciaPanel />} />
        <Route path="agencia/clientes" element={<AgenciaClientes />} />
        <Route path="agencia/clientes/:id" element={<AgenciaClienteDetalle />} />
        <Route path="agencia/cobros" element={<AgenciaCobros />} />
        <Route path="agencia/renovaciones" element={<AgenciaRenovaciones />} />
        <Route path="agencia/egresos" element={<AgenciaEgresos />} />
        <Route path="agencia/finanzas" element={<AgenciaFinanzas />} />
        <Route path="agencia/abonos" element={<AgenciaAbonos />} />
        <Route path="agencia/medios-pago" element={<AgenciaMediosPago />} />
        <Route path="agencia/propuestas" element={<AgenciaPropuestas />} />
        <Route path="settings" element={<AdminSettings />} />
      </Route>
    </Routes>);
  }
  return (<Routes>
    <Route path="/login" element={user ? <Navigate to="/" /> : <Login />} />
    <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
      <Route index element={<FeatureRoute feature="dashboard"><Dashboard /></FeatureRoute>} />
      <Route path="datasets" element={<FeatureRoute feature="datasets"><Datasets /></FeatureRoute>} />
      <Route path="datasets/:id" element={<FeatureRoute feature="datasets"><DatasetView /></FeatureRoute>} />
      <Route path="leads" element={<FeatureRoute feature="leads"><AllLeads /></FeatureRoute>} />
      <Route path="inbox" element={<FeatureRoute feature="inbox"><Inbox /></FeatureRoute>} />
      <Route path="templates" element={<FeatureRoute feature="templates"><Templates /></FeatureRoute>} />
      <Route path="reports" element={<FeatureRoute feature="reports"><Reports /></FeatureRoute>} />
      <Route path="campaigns" element={<FeatureRoute feature="campaigns"><Campaigns /></FeatureRoute>} />
      <Route path="bot" element={<FeatureRoute feature="bot"><BotConfig /></FeatureRoute>} />
      <Route path="channels" element={<FeatureRoute feature="channels"><Channels /></FeatureRoute>} />
      <Route path="settings" element={<FeatureRoute feature="settings"><Settings /></FeatureRoute>} />
    </Route>
  </Routes>);
}

export default function App() {
  // El portal de clientes usa su propio login, separado del CRM
  if (isPortalHost()) {
    return (<BrowserRouter>
      <Toaster position="top-right" toastOptions={{ style: { background:'var(--tooltip-bg)', color:'var(--text-primary)', border:'1px solid var(--border)', fontFamily:'Raleway', boxShadow:'var(--shadow)' } }} />
      <PortalApp />
    </BrowserRouter>);
  }

  return (<AuthProvider><BrowserRouter>
    <Toaster position="top-right" toastOptions={{ style: { background:'var(--tooltip-bg)', color:'var(--text-primary)', border:'1px solid var(--border)', fontFamily:'Raleway', boxShadow:'var(--shadow)' }, success: { iconTheme: { primary:'#22c55e', secondary:'#fff' } }, error: { iconTheme: { primary:'#ef4444', secondary:'#fff' } } }} />
    <AppRoutes />
  </BrowserRouter></AuthProvider>);
}
